import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Upload, X, ChevronUp, ChevronDown, CheckCircle2, AlertCircle,
} from "lucide-react";
import { useUploadManager } from "@/context/UploadManagerContext";

function humanBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * Floating pill at the bottom-right of every page that shows every
 * in-flight upload managed by ``UploadManagerContext``. Persists
 * across route changes so an admin can start a 600-file upload, go
 * back to Jobs, open another job, and still see the running progress.
 */
export default function FloatingUploadDock() {
  const { uploads, cancelUpload, dismissUpload } = useUploadManager();
  const [collapsed, setCollapsed] = useState(false);

  const entries = useMemo(
    () => Object.values(uploads).sort((a, b) => b.startedAt - a.startedAt),
    [uploads],
  );

  if (entries.length === 0) return null;

  const activeCount = entries.filter(
    (u) => u.phase !== "done" && u.phase !== "cancelled",
  ).length;

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-[380px] max-w-[calc(100vw-2rem)] rounded-md border border-border bg-white shadow-xl"
      data-testid="upload-dock"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 border-b border-border hover:bg-slate-50 transition-colors"
        data-testid="upload-dock-toggle"
      >
        <div className="flex items-center gap-2">
          <Upload className="h-4 w-4 text-primary" />
          <span className="text-sm font-bold text-slate-800">
            {activeCount > 0
              ? `${activeCount} upload${activeCount === 1 ? "" : "s"} running`
              : `${entries.length} finished upload${entries.length === 1 ? "" : "s"}`}
          </span>
        </div>
        {collapsed ? (
          <ChevronUp className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        )}
      </button>
      {!collapsed && (
        <div className="max-h-[52vh] overflow-auto divide-y divide-border">
          {entries.map((u) => {
            const active = u.phase !== "done" && u.phase !== "cancelled";
            return (
              <div
                key={u.id}
                className="px-4 py-3"
                data-testid={`upload-dock-item-${u.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/jobs/${u.jobId}`}
                      className="text-sm font-bold text-slate-800 hover:text-primary block truncate"
                    >
                      {u.jobName || u.jobId}
                    </Link>
                    <p className="text-[11px] text-slate-500 font-semibold">
                      {u.folder}
                      {" · "}
                      {u.done}
                      {" / "}
                      {u.fileCount}
                      {u.failed > 0 && (
                        <span className="text-red-600">
                          {" · "}
                          {u.failed}
                          {" failed"}
                        </span>
                      )}
                      {" · "}
                      {humanBytes(u.totalBytes)}
                    </p>
                  </div>
                  {active ? (
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => {
                        if (window.confirm("Cancel this upload? Progress will be lost.")) {
                          cancelUpload(u.id);
                        }
                      }}
                      className="h-6 w-6 text-slate-400 hover:text-red-600"
                      data-testid={`upload-dock-cancel-${u.id}`}
                      title="Cancel upload"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => dismissUpload(u.id)}
                      className="h-6 w-6 text-slate-400 hover:text-slate-700"
                      data-testid={`upload-dock-dismiss-${u.id}`}
                      title="Dismiss"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {active ? (
                  <>
                    <Progress value={u.progressPct} className="h-1.5" />
                    <p className="text-[11px] text-slate-500 font-medium mt-1">
                      {u.phase === "saving"
                        ? "Finalising on Cloudflare R2…"
                        : u.phase === "retrying"
                          ? `Retrying failed uploads…`
                          : `${u.progressPct}% — you can navigate away, this keeps running`}
                    </p>
                  </>
                ) : u.phase === "cancelled" ? (
                  <p className="text-[11px] text-slate-500 font-semibold flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Cancelled
                  </p>
                ) : u.failed > 0 ? (
                  <p className="text-[11px] text-orange-700 font-semibold flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Done — {u.failed} failed
                  </p>
                ) : (
                  <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    All {u.done} files uploaded
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
